import React from 'react';
import { useMutation } from '@apollo/client';
import { Button, message } from 'antd';
import {
  useGoogleLogin,
  useGoogleOneTapLogin,
  CredentialResponse,
  CodeResponse,
} from '@react-oauth/google';
import { LOG_IN_WITH_GOOGLE } from '../../graphql/mutations/mutations';
import { IUser } from '../../types/user';
import { googleLoginButtonStyles, googleLogoStyles } from './authStyles';

interface AuthWithSocialProps {
  onComplete: (user: IUser) => void;
  onError?: (error: { message?: string }) => void;
  onLoading?: (loading: boolean) => void;
  successMessage?: string;
  errorMessage?: string;
  loading?: boolean;
  type: 'login' | 'register';
}

export const AuthWithSocial: React.FC<AuthWithSocialProps> = ({
  onComplete,
  onError,
  onLoading,
  successMessage = 'Authentication successful!',
  errorMessage = 'Google authentication failed',
  type = 'login',
  loading = false,
}) => {
  const handleError = (error: { message?: string }) => {
    const errorMsg = error.message || errorMessage;
    message.error(errorMsg);
    onLoading?.(false);
    onError?.(error);
  };

  const [loginWithGoogle] = useMutation(LOG_IN_WITH_GOOGLE, {
    onCompleted: data => {
      if (data?.LogInWithGoogle?.email) {
        message.success(successMessage);
        onComplete(data.LogInWithGoogle);
      } else {
        handleError({ message: errorMessage });
      }
      onLoading?.(false);
    },
    onError: handleError,
  });

  const handleGoogleSuccess = (credentialResponse: CredentialResponse | CodeResponse) => {
    onLoading?.(true);
    loginWithGoogle({
      variables: {
        code: (credentialResponse as CodeResponse).code,
        idToken: (credentialResponse as CredentialResponse).credential,
      },
    });
  };

  const handleGoogleError = () => {
    handleError({ message: errorMessage });
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
    flow: 'auth-code',
  });

  useGoogleOneTapLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => {
      handleError({ message: 'Login Failed' });
    },
  });

  return (
    <Button
      className={googleLoginButtonStyles}
      onClick={() => googleLogin()}
      disabled={loading}
      loading={loading}>
      <GoogleIcon />
      {type == 'login' ? 'Sign in with Google' : 'Sign up with Google'}
    </Button>
  );
};

const GoogleIcon = () => (
  <svg className={googleLogoStyles} viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);
